/**
 * @fileoverview Memory Game Result Overlay
 * @description Modal shown when the memory game is completed
 */

import React, { useEffect } from "react";
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  Animated,
  Dimensions,
} from "react-native";
import LottieView from "lottie-react-native";
import { Ionicons } from "@expo/vector-icons";
import { MemoryGameResult } from "../types";

interface MemoryResultOverlayProps {
  visible: boolean;
  result: MemoryGameResult | null;
  mode?: "free" | "weekend";
  weekendPoints?: number;
  isVersusMode?: boolean;
  isRaceWinner?: boolean | null;
  opponentName?: string;
  opponentTime?: number | null;
  waitingForOpponent?: boolean;
  onPlayAgain: () => void;
  onExit: () => void;
  onClose: () => void;
  onRefresh?: () => void;
}

const { width } = Dimensions.get("window");

/**
 * Format seconds to MM:SS
 */
const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

/**
 * MemoryResultOverlay - Completion celebration
 */
export const MemoryResultOverlay: React.FC<MemoryResultOverlayProps> = ({
  visible,
  result,
  mode = "free",
  weekendPoints,
  isVersusMode = false,
  isRaceWinner = null,
  opponentName,
  opponentTime,
  waitingForOpponent = false,
  onPlayAgain,
  onExit,
  onClose,
  onRefresh,
}) => {
  const scaleAnim = React.useRef(new Animated.Value(0)).current;
  const fadeAnim = React.useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (visible) {
      Animated.parallel([
        Animated.spring(scaleAnim, {
          toValue: 1,
          friction: 5,
          tension: 100,
          useNativeDriver: true,
        }),
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 300,
          useNativeDriver: true,
        }),
      ]).start();
    } else {
      scaleAnim.setValue(0);
      fadeAnim.setValue(0);
    }
  }, [visible, scaleAnim, fadeAnim]);

  if (!visible || !result) return null;

  const isPerfect = result.isPerfect;
  const efficiency = result.pairs > 0 ? (result.pairs / result.moves) * 100 : 0;

  // Determine title/emoji based on game mode
  const getResultConfig = () => {
    if (isVersusMode) {
      // Show waiting state if opponent hasn't finished yet
      if (waitingForOpponent) {
        return {
          emoji: "🤔",
          title: "Nog even geduld...",
          subtitle: "Wie zal er gewonnen hebben?",
          color: "#8B5CF6",
          lottieSource: require("@/assets/lottie/finger_crossed.json"),
          showButtons: false,
        };
      }
      
      if (isRaceWinner === true) {
        return {
          emoji: "🏆",
          title: "Gewonnen!",
          subtitle: `Je was sneller dan ${opponentName}!`,
          color: "#22C55E",
          lottieSource: require("@/assets/lottie/confetti.json"),
          showButtons: true,
        };
      } else if (isRaceWinner === false) {
        return {
          emoji: "😔",
          title: "Verloren",
          subtitle: `${opponentName} was deze keer sneller`,
          color: "#EF4444",
          lottieSource: require("@/assets/lottie/Holding_Back_Tears.json"),
          showButtons: true,
        };
      } else {
        // Fallback waiting state
        return {
          emoji: "⏳",
          title: "Klaar!",
          subtitle: `Wachten tot ${opponentName} klaar is...`,
          color: "#F59E0B",
          lottieSource: require("@/assets/lottie/finger_crossed.json"),
          showButtons: false,
        };
      }
    }

    // Solo mode
    if (isPerfect) {
      return {
        emoji: "🏆",
        title: "Perfect!",
        subtitle: "Alle paren in één keer gevonden!",
        color: "#F59E0B",
        lottieSource: require("@/assets/lottie/confetti.json"),
        showButtons: true,
      };
    }
    return {
      emoji: "🎉",
      title: "Gefeliciteerd!",
      subtitle: "Je hebt alle paren gevonden!",
      color: "#22C55E",
      lottieSource: require("@/assets/lottie/completed.json"),
      showButtons: true,
    };
  };

  const resultConfig = getResultConfig();

  return (
    <Modal
      visible={visible}
      transparent
      animationType="none"
      onRequestClose={onClose}
    >
      <Animated.View style={[styles.backdrop, { opacity: fadeAnim }]}>
        <TouchableOpacity
          style={styles.backdropTouch}
          activeOpacity={1}
          onPress={onClose}
        />

        <Animated.View
          style={[
            styles.container,
            {
              transform: [{ scale: scaleAnim }],
              borderColor: resultConfig.color,
            },
          ]}
        >
          {/* Lottie Animation */}
          <View style={styles.lottieContainer}>
            <LottieView
              source={resultConfig.lottieSource}
              autoPlay
              loop={waitingForOpponent || isRaceWinner === true || isPerfect}
              style={styles.lottie}
            />
          </View>

          {/* Title */}
          <Text
            style={[
              styles.title,
              { color: resultConfig.color },
            ]}
          >
            {resultConfig.title}
          </Text>
          <Text style={styles.subtitle}>
            {resultConfig.subtitle}
          </Text>

          {/* Stats - only show when race is complete */}
          {resultConfig.showButtons && (
            <View style={styles.statsContainer}>
              {/* Versus Time Comparison */}
              {isVersusMode && opponentTime !== null && opponentTime !== undefined && (
                <View style={styles.versusCompare}>
                  <View style={styles.versusPlayerTime}>
                    <Text style={styles.versusLabel}>Jij</Text>
                    <Text style={[styles.versusTime, { color: isRaceWinner ? "#22C55E" : "#EF4444" }]}>
                      {formatTime(result.timeSeconds)}
                    </Text>
                  </View>
                  <Text style={styles.versusVs}>vs</Text>
                  <View style={styles.versusPlayerTime}>
                    <Text style={styles.versusLabel}>{opponentName}</Text>
                    <Text style={[styles.versusTime, { color: isRaceWinner === false ? "#22C55E" : "#EF4444" }]}>
                      {formatTime(opponentTime)}
                    </Text>
                  </View>
                </View>
              )}

              <View style={styles.statRow}>
                <View style={styles.statItem}>
                  <Ionicons name="time" size={20} color="#3B82F6" />
                  <Text style={styles.statValue}>
                    {formatTime(result.timeSeconds)}
                  </Text>
                  <Text style={styles.statLabel}>Jouw Tijd</Text>
                </View>

                <View style={styles.statItem}>
                  <Ionicons name="swap-horizontal" size={20} color="#8B5CF6" />
                  <Text style={styles.statValue}>{result.moves}</Text>
                  <Text style={styles.statLabel}>Zetten</Text>
                </View>

                <View style={styles.statItem}>
                  <Ionicons name="analytics" size={20} color="#EC4899" />
                  <Text style={styles.statValue}>{Math.round(efficiency)}%</Text>
                  <Text style={styles.statLabel}>Efficiëntie</Text>
                </View>
              </View>

              {/* Weekend Points - only show for weekend mode when won */}
              {mode === "weekend" && weekendPoints !== undefined && weekendPoints > 0 && (
                <View style={styles.scoreContainer}>
                  <Text style={styles.scoreLabel}>Weekend Punten</Text>
                  <Text style={styles.scoreValue}>+{weekendPoints}</Text>
                  <Text style={styles.scorePoints}>voor je team!</Text>
                </View>
              )}
            </View>
          )}

          {/* Bonuses earned - only show for weekend mode */}
          {resultConfig.showButtons && mode === "weekend" && isPerfect && (
            <View style={styles.bonusBadge}>
              <Ionicons name="star" size={16} color="#F59E0B" />
              <Text style={styles.bonusText}>Perfect Bonus verdiend!</Text>
            </View>
          )}

          {/* Waiting hint when opponent hasn't finished */}
          {waitingForOpponent && (
            <View style={styles.waitingHint}>
              <Text style={styles.waitingHintText}>
                Jouw tijd: {formatTime(result.timeSeconds)}
              </Text>
              <Text style={styles.waitingHintSubtext}>
                Wacht even tot {opponentName} ook klaar is...
              </Text>
              
              {/* Refresh button */}
              {onRefresh && (
                <TouchableOpacity
                  style={styles.refreshButton}
                  onPress={onRefresh}
                  accessibilityLabel="Vernieuwen"
                  accessibilityRole="button"
                >
                  <Ionicons name="refresh" size={18} color="#8B5CF6" />
                  <Text style={styles.refreshButtonText}>Resultaat ophalen</Text>
                </TouchableOpacity>
              )}
            </View>
          )}

          {/* Action Buttons - only show when race is complete */}
          {resultConfig.showButtons && (
            <View style={styles.buttonContainer}>
              <TouchableOpacity
                style={[styles.button, styles.playAgainButton]}
                onPress={onPlayAgain}
                accessibilityLabel="Opnieuw spelen"
                accessibilityRole="button"
              >
                <Ionicons name="refresh" size={20} color="#FFFFFF" />
                <Text style={styles.playAgainButtonText}>Opnieuw</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.button, styles.exitButton]}
                onPress={onExit}
                accessibilityLabel="Afsluiten"
                accessibilityRole="button"
              >
                <Text style={styles.exitButtonText}>Afsluiten</Text>
              </TouchableOpacity>
            </View>
          )}
        </Animated.View>
      </Animated.View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.8)",
    justifyContent: "center",
    alignItems: "center",
  },
  backdropTouch: {
    ...StyleSheet.absoluteFillObject,
  },
  container: {
    width: width * 0.88,
    maxWidth: 360,
    backgroundColor: "#1F2937",
    borderRadius: 24,
    padding: 24,
    alignItems: "center",
    borderWidth: 3,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.4,
    shadowRadius: 16,
    elevation: 10,
  },
  lottieContainer: {
    width: 100,
    height: 100,
    marginBottom: 4,
  },
  lottie: {
    width: "100%",
    height: "100%",
  },
  emoji: {
    fontSize: 48,
    marginBottom: 8,
  },
  title: {
    fontSize: 28,
    fontWeight: "bold",
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: "#9CA3AF",
    textAlign: "center",
    marginBottom: 20,
  },
  statsContainer: {
    width: "100%",
    marginBottom: 16,
  },
  statRow: {
    flexDirection: "row",
    justifyContent: "space-around",
    marginBottom: 16,
  },
  versusCompare: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 16,
    gap: 16,
    padding: 12,
    backgroundColor: "rgba(0, 0, 0, 0.3)",
    borderRadius: 12,
  },
  versusPlayerTime: {
    alignItems: "center",
    flex: 1,
  },
  versusLabel: {
    fontSize: 12,
    color: "#9CA3AF",
    marginBottom: 4,
  },
  versusTime: {
    fontSize: 24,
    fontWeight: "700",
  },
  versusVs: {
    fontSize: 14,
    fontWeight: "600",
    color: "#6B7280",
  },
  statItem: {
    alignItems: "center",
  },
  statValue: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#FFFFFF",
    marginTop: 4,
  },
  statLabel: {
    fontSize: 11,
    color: "#9CA3AF",
    marginTop: 2,
  },
  scoreContainer: {
    backgroundColor: "#374151",
    borderRadius: 16,
    padding: 16,
    alignItems: "center",
  },
  scoreLabel: {
    fontSize: 12,
    color: "#9CA3AF",
    marginBottom: 4,
  },
  scoreValue: {
    fontSize: 36,
    fontWeight: "bold",
    color: "#22C55E",
  },
  scorePoints: {
    fontSize: 14,
    color: "#22C55E",
  },
  bonusBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    backgroundColor: "rgba(245, 158, 11, 0.15)",
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    marginBottom: 20,
  },
  bonusText: {
    fontSize: 13,
    fontWeight: "600",
    color: "#F59E0B",
  },
  waitingHint: {
    alignItems: "center",
    marginTop: 20,
    paddingHorizontal: 20,
  },
  waitingHintText: {
    fontSize: 18,
    fontWeight: "600",
    color: "#E5E7EB",
    marginBottom: 8,
  },
  waitingHintSubtext: {
    fontSize: 14,
    color: "#9CA3AF",
    textAlign: "center",
  },
  refreshButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    marginTop: 16,
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: "#8B5CF6",
    backgroundColor: "rgba(139, 92, 246, 0.1)",
  },
  refreshButtonText: {
    fontSize: 14,
    fontWeight: "600",
    color: "#8B5CF6",
  },
  buttonContainer: {
    flexDirection: "row",
    gap: 12,
    width: "100%",
  },
  button: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    paddingVertical: 14,
    borderRadius: 12,
  },
  playAgainButton: {
    backgroundColor: "#8B5CF6",
  },
  playAgainButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
  exitButton: {
    backgroundColor: "#374151",
  },
  exitButtonText: {
    color: "#E5E7EB",
    fontSize: 16,
    fontWeight: "600",
  },
});

export default MemoryResultOverlay;

